import { consoleChunks } from "../lib/console-loaders";
import { needsSupabaseAuthAtBoot, preloadSupabaseAuth } from "../lib/supabase";
import type { BootPlan } from "./first-screen";
import { preloadable } from "./preloadable";

// The lazily loaded screens a page load can start on (ADR-WBC1). main.tsx
// loads the first screen's module before the first render, so the prerendered
// splash (or docs snapshot) is replaced by the real screen in one commit,
// never by a Suspense fallback. The head script has already started fetching
// the same files (src/boot/boot-modules.ts lists them for the build).

export const SplashScreen = preloadable(() =>
  import("../components/PublicSplash").then((module) => ({ default: module.SplashScreen }))
);

/**
 * Console stylesheet and fonts; Vite resolves the import once the CSS has
 * loaded. The shared console loader (src/lib/console-loaders.ts, P-07) also
 * starts the console message part and the console-only components with it
 * (ADR-WES1), and remembers the module: the console bootstrap and the sign-in
 * submit start it earlier, and the first render then needs no Suspense round.
 */
export const ConsoleSurface = preloadable(() => consoleChunks.surface(), undefined, consoleChunks.surface.peek);

export const DocsApp = preloadable(() => import("../docs/DocsApp").then((module) => ({ default: module.DocsApp })));

/**
 * Loads what the first screen renders. Resolves even if a load fails: the screen retries and reports it.
 *
 * ADR-WES1: the auth client is a lazy chunk that only a boot holding a session
 * (or an auth callback in the URL) downloads. It starts here, beside the first
 * screen, instead of after the first render; the auth check waits for it
 * either way, and a visitor without a session never fetches it.
 */
export function preloadFirstScreen(plan: BootPlan): Promise<void> {
  // The pre-launch teaser (VITE_TEASER_MODE) renders its own page, not these screens.
  if (import.meta.env.VITE_TEASER_MODE === "true") return Promise.resolve();
  if (needsSupabaseAuthAtBoot()) preloadSupabaseAuth();
  const loads =
    plan.screen === "splash"
      ? [SplashScreen.preload()]
      : plan.screen === "dashboard"
        ? [ConsoleSurface.preload()]
        : plan.screen === "docs"
          ? [ConsoleSurface.preload(), DocsApp.preload()]
          : [];
  return Promise.all(loads).then(
    () => undefined,
    () => undefined
  );
}
